import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { MultiSelectItem } from '../models/multi-select-item';
import {
    CAN_ACCESS_ACCOUNT_MANAGEMENT,
    CAN_ACCESS_INTEGRATIONS,
    CAN_ACCESS_PROMPT_TEMPLATES,
    CAN_ACCESS_PUBLISH_PROMPTS,
    CAN_ACCESS_USER_MANAGEMENT,
    Rights,
} from '../models/rights';
import { UserProfileService } from './user-profile.service';

@Injectable({
    providedIn: 'root',
})
export class RightsService {
    private readonly userProfileService = inject(UserProfileService);

    private allRights: Array<string> = [
        CAN_ACCESS_ACCOUNT_MANAGEMENT,
        CAN_ACCESS_USER_MANAGEMENT,
        CAN_ACCESS_INTEGRATIONS,
        CAN_ACCESS_PROMPT_TEMPLATES,
        CAN_ACCESS_PUBLISH_PROMPTS,
    ];

    public getRightsOfCurrentUser(): Observable<Rights> {
        return this.userProfileService.getOwnUserProfile().pipe(map((userProfile) => userProfile.rights ?? {}));
    }

    public hasAtLeastOneRightAssigned(): Observable<boolean> {
        return this.getRightsOfCurrentUser().pipe(
            map((rights) => {
                return Object.values(rights).find((a) => a === true) ?? false;
            }),
        );
    }

    public getAllRightsAsMultiSelectItems(): Observable<MultiSelectItem[]> {
        const rightsItems = this.allRights.map((right) => {
            const multiselectItem: MultiSelectItem = {
                uid: right,
                name: this.getRightName(right),
            };
            return multiselectItem;
        });
        return of(rightsItems);
    }

    public transformMultiSelectItemsIntoRights(multiSelectItems: MultiSelectItem[]): Rights {
        const rights: Rights = {};
        multiSelectItems.forEach((item) => {
            rights[item.uid as keyof Rights] = true;
        });
        return rights;
    }

    public transformUserProfileRightsIntoMultiSelectItems(rights: Rights | undefined): Observable<MultiSelectItem[]> {
        if (!rights) {
            return of([]);
        }
        return this.getAllRightsAsMultiSelectItems().pipe(
            map((allRightsAsMultiSelectItems) => {
                return allRightsAsMultiSelectItems.filter((rightAsMultiSelectItem) => {
                    return rights[rightAsMultiSelectItem.uid as keyof Rights];
                });
            }),
        );
    }

    public getLabelsForRights(rights: Rights): Observable<string[]> {
        const rightsNames: string[] = [];
        if (rights.canAccessAccountManagement) {
            rightsNames.push('Organisationsverwaltung');
        }
        if (rights.canAccessUserManagement) {
            rightsNames.push('Nutzerverwaltung');
        }
        if (rights.canAccessIntegrations) {
            rightsNames.push('Integrationen');
        }
        if (rights.canAccessPromptTemplates) {
            rightsNames.push('Promptvorlagen');
        }
        if (rights.canAccessPublishPrompts) {
            rightsNames.push('Öffentliche Prompts');
        }
        return of(rightsNames);
    }

    private getRightName(right: string): string {
        switch (right) {
            case CAN_ACCESS_ACCOUNT_MANAGEMENT:
                return 'Organisationsverwaltung';
            case CAN_ACCESS_USER_MANAGEMENT:
                return 'Nutzerverwaltung';
            case CAN_ACCESS_INTEGRATIONS:
                return 'Integrationen';
            case CAN_ACCESS_PROMPT_TEMPLATES:
                return 'Promptvorlagen';
            case CAN_ACCESS_PUBLISH_PROMPTS:
                return 'Öffentliche Prompts';
            default:
                return right;
        }
    }

    public transformMultiSelectItemsIntoUserProfileRights(multiSelectItems: MultiSelectItem[]): Rights {
        const result: Rights = {};
        for (let item of multiSelectItems) {
            result[item.uid as keyof typeof result] = true;
        }
        return result;
    }
}
